/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DocumentType,
  getDocumentConfig,
  getDocumentFilename,
  normalizeLanguage,
} from '@shared/utils/documentVersions';

const shouldLog = import.meta.env.DEBUG === 'true';

/**
 * Conditionally logs a message when not running in the test environment.
 * @param level - The console method to use
 * @param args - Arguments forwarded to console
 */
function logIfEnabled(
  level: 'log' | 'warn' | 'error',
  ...args: unknown[]
): void {
  if (shouldLog) {
    console[level](...args);
  }
}

/**
 * Detects whether a fetch response contains HTML instead of the expected
 * markdown content. This guards against SPA fallback behaviour where the
 * hosting server returns `index.html` (HTTP 200) for missing static files.
 */
function isHtmlResponse(response: Response, body: string): boolean {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('text/html')) return true;
  const trimmed = body.trimStart().toLowerCase();
  return trimmed.startsWith('<!doctype') || trimmed.startsWith('<html');
}

/**
 * Fetches a document and validates that the response is actual markdown
 * content (not an HTML SPA fallback). Returns `null` when the response is
 * missing or contains HTML so the caller can attempt a fallback.
 */
async function fetchMarkdown(
  url: string,
): Promise<string | null> {
  const response = await fetch(url);
  if (!response.ok) return null;
  const body = await response.text();
  if (isHtmlResponse(response, body)) return null;
  if (!body.trim()) return null;
  return body;
}

/**
 * Fetches a document, falling back to the default filename on 404 or when the
 * server returns HTML instead of markdown (SPA fallback).
 * @param filename - Primary filename to fetch
 * @param defaultFilename - Fallback filename if primary is not found
 * @param documentType - The document type, used in log messages
 * @returns The document body text
 */
async function fetchWithFallback(
  filename: string,
  defaultFilename: string,
  documentType: DocumentType
): Promise<string> {
  const primary = await fetchMarkdown(filename);
  if (primary !== null) return primary;

  logIfEnabled(
    'warn',
    `[${documentType}] File not found or returned HTML: ${filename}, falling back to default: ${defaultFilename}`
  );

  const fallback = await fetchMarkdown(defaultFilename);
  if (fallback !== null) return fallback;

  throw new Error(
    `Failed to load document: both ${filename} and ${defaultFilename} returned HTML or were not found`
  );
}

/**
 * Converts an unknown thrown value to an Error instance.
 * @param err - The caught value
 * @returns An Error object
 */
function toError(err: unknown): Error {
  return err instanceof Error ? err : new Error(String(err));
}

/**
 * Hook for loading document content based on document type and current language.
 *
 * @param documentType - The type of document to load
 * @param fallbackMessage - Optional fallback message if content fails to load
 * @param version - Optional version number for versioned path resolution
 * @returns The document content and loading state
 */
export function useDocumentContent(
  documentType: DocumentType,
  fallbackMessage?: string,
  version?: number
): { content: string; isLoading: boolean; error: Error | null } {
  const { i18n } = useTranslation();
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadContent = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const language = i18n.language || 'en';
        const normalizedLang = normalizeLanguage(language);
        const config = getDocumentConfig(documentType);
        const filename = getDocumentFilename(documentType, normalizedLang, version);
        const defaultFilename = getDocumentFilename(documentType, config.defaultLanguage, version);

        logIfEnabled(
          'log',
          `[${documentType}] Loading for language: ${language} -> ${normalizedLang}, file: ${filename}`
        );

        const response = await fetchWithFallback(filename, defaultFilename, documentType);
        logIfEnabled('log', `[${documentType}] Content loaded successfully (${response.length} chars)`);
        setContent(response);
      } catch (err) {
        const loadError = toError(err);
        logIfEnabled('error', `[${documentType}] Error loading content:`, loadError);
        setError(loadError);
        setContent(fallbackMessage ?? '');
      } finally {
        setIsLoading(false);
      }
    };

    loadContent();
  }, [i18n.language, documentType, fallbackMessage, version]);

  return { content, isLoading, error };
}
